import { useQueryCandidateInfo } from "@/api/candidate/candidate";
import { CandidateRegistrationDtoClient } from "@/api/candidate/dto/candidate-registration.dto";
import { Button } from "@/components/atoms/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/atoms/Dialog";
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/atoms/Form";
import AddressForm from "@/components/molecules/AddressForm/AddressForm";
import { mandatoryAddressSchema } from "@/components/molecules/AddressForm/AddressFormSchema";
import DatePicker from "@/components/molecules/DatePicker";
import { PayementUtil } from "@/lib/payement-util";
import { RegistrationUtil } from "@/lib/registration-util";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  PayementAcknowledgeSchema,
  payementAcknowledgeDefaultValues,
  payementAcknowledgeSchema,
} from "./payement-acknowledge.schema";
import PayerRadio from "./PayerRadio";
import TransferWarnings from "./TransferWarnings";
import usePayementTransferStatus from "./usePayementTransferStatus";

interface PayementAcknowledgeDialogProps {
  registration: CandidateRegistrationDtoClient;
  candidateId: number;
}

const PayementAcknowledgeDialog = ({
  registration,
  candidateId,
}: PayementAcknowledgeDialogProps) => {
  const [open, setOpen] = useState(false);

  const { data: candidateInfo } = useQueryCandidateInfo(candidateId);

  const isAcknowledging = PayementUtil.isWaitingValidation(
    registration.payementStatus,
  );
  const payingCompany = RegistrationUtil.hasNonRefusedDelegation(registration)
    ? registration.delegation?.company
    : undefined;
  const canChoosePayer = !isAcknowledging && !!payingCompany;

  const transferStatusMutation = usePayementTransferStatus({
    candidateId,
    successMessage: isAcknowledging
      ? "Virement accepté"
      : "Paiement enregistré",
    onSuccess: () => setOpen(false),
  });

  const schema = useMemo(
    () => payementAcknowledgeSchema(registration),
    [registration],
  );

  const form = useForm<PayementAcknowledgeSchema>({
    resolver: zodResolver(schema),
    defaultValues: payementAcknowledgeDefaultValues(
      registration,
      candidateInfo?.address,
    ),
  });

  const payer = useWatch({ control: form.control, name: "payer" });
  const missingBillingAddress = RegistrationUtil.missingBillingAddress(
    registration,
    payer,
  );

  const onSubmit = ({
    date,
    payer,
    billingAddress,
  }: PayementAcknowledgeSchema) =>
    transferStatusMutation.mutate({
      registrationId: registration.id,
      accepted: true,
      transferDate: date,
      payer: canChoosePayer ? payer : undefined,
      // the schema's superRefine already enforced the mandatory shape
      billingAddress: missingBillingAddress
        ? mandatoryAddressSchema.parse(billingAddress)
        : undefined,
    });

  const title = isAcknowledging
    ? "Reconnaissance du virement"
    : "Enregistrer un paiement par virement";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        // also on open, so a late candidate query still prefills the form
        form.reset(
          payementAcknowledgeDefaultValues(
            registration,
            candidateInfo?.address,
          ),
        );
      }}
    >
      <DialogTrigger asChild>
        <Button>
          {isAcknowledging ? "Accuser réception" : "Marquer payé par virement"}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-96 max-h-[85%] overflow-auto p-4">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription hidden={true}>{title}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex w-full flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="date"
              render={() => (
                <FormItem className="w-full">
                  <DatePicker placeholder="Date du virement" />
                  <FormMessage />
                </FormItem>
              )}
            />
            {canChoosePayer && payingCompany && (
              <PayerRadio name="payer" companyName={payingCompany.name} />
            )}
            <TransferWarnings registration={registration} payer={payer} />
            {missingBillingAddress && (
              <AddressForm
                fieldName="billingAddress"
                title="Adresse de facturation"
                isMandatory={true}
              />
            )}
            <div className="flex flex-row w-full gap-4">
              <Button
                variant="red"
                onClick={() => setOpen(false)}
                type="button"
                className="w-1/2"
                disabled={transferStatusMutation.isPending}
              >
                Annuler
              </Button>
              <Button
                variant="green"
                type="submit"
                className="w-1/2"
                disabled={transferStatusMutation.isPending}
              >
                {transferStatusMutation.isPending ? "En cours..." : "Valider"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PayementAcknowledgeDialog;
